import React, { useState, useEffect } from "react";
import { Table, Button, Popconfirm, Switch, Space, Typography, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { DiscountCouponModel } from "../models/discountCoupon";
import DiscountCouponModal from "../components/discount-coupon/DiscountCouponModal";
import { apiCall } from "../helpers/apiHelper";
import useTranslation from "../hooks/TranslationContext";

//apis
import * as DiscountCouponApi from '../apis/discountCouponApi';
import { ApiResponse } from "../models/response";


const { Text } = Typography;

export default function DiscountCouponsPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [coupons, setCoupons] = useState<DiscountCouponModel[]>([]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState<DiscountCouponModel | null>(null);
  const abortController = new AbortController();

  useEffect(() => {
    getDiscountCouponsAsync();
  }, []);

  const getDiscountCoupons = async (abortSignal?: AbortSignal) => {
    const response = await apiCall(DiscountCouponApi.getList(abortSignal));

    return response.success ? DiscountCouponModel.toArrayOfClass(response.data?.value || []) : [];
  };

  const getDiscountCouponsAsync = async () => {
    setLoading(true);
    setCoupons(await getDiscountCoupons(abortController.signal));
    setLoading(false);
  }

  const handleDelete = async (id: number) => {
    setLoading(true);
    const response = await apiCall(DiscountCouponApi.deleteItem(id));
    setLoading(false);

    if (response.success) {
      message.success(t("item-deleted-successfully"));
      getDiscountCouponsAsync();
    }
  };

  const onToggleActive = async (record: any, isActive: boolean) => {
    const data = {...record, isActive};

    setLoading(true);
    let response: ApiResponse = await apiCall(DiscountCouponApi.updateItem(data?.discountCouponId!,  data, abortController.signal));

    if (response.success) {
      message.success(t(isActive ? 'discount-coupon-activated-successfully' : 'discount-coupon-deactivated-successfully'));
      getDiscountCouponsAsync();
    } else
      message.error(response.error?.value);

    setLoading(false);
  };

  const onCreateClick = () => {
    setEditData(null);
    setModalOpen(true);
  };

  const onEdit = (coupon: DiscountCouponModel) => {
    setEditData(coupon);
    setModalOpen(true);
  };

  const onModalSave = () => {
    setModalOpen(false);
    setEditData(null);
    getDiscountCouponsAsync();
  };

  const columns: ColumnsType<DiscountCouponModel> = [
    {
      title: t("code"),
      dataIndex: "code",
      key: "code",
      render: (text: string) => <Text strong>{text}</Text>,
      responsive: ["xs", "sm", "md", "lg"], // Corrected type to Breakpoint[]
    },
    {
      title: t("type"),
      dataIndex: "type",
      key: "type",
      render: (type: number) => t(type === 0 ? "fixed-amount" : "percentage"),
      responsive: ["sm", "md", "lg"],
    },
    {
      title: t("amount-percent"),
      key: "amount",
      render: (text: number, record: any) => record.type === 0 ? (record.amount?.toLocaleString() || "-") : (`${record.discountPercent}%`),
      responsive: ["sm", "md", "lg"],
    },
    // {
    //   title: `${t("count")}/${t("usage-limit")}`,
    //   key: "count",
    //   render: (text: number, record: any) => `${(record.usageCount || 0)}/${(record.usageLimit || 0)}`,
    //   responsive: ["sm", "md", "lg"],
    // },
    {
      title: t("usage-count"),
      dataIndex: "usageCount",
      key: "usageCount",
      render: (text: number) => text || 0,
      responsive: ["sm", "md", "lg"],
    },
    {
      title: t("usage-limit"),
      dataIndex: "usageLimit",
      key: "usageLimit",
      render: (text: number) => text || 0,
      responsive: ["sm", "md", "lg"],
    },
    {
      title: t("active"),
      key: "isActive",
      render: (_: any, record: DiscountCouponModel) => (
        <Switch
          checked={record.isActive}
          onChange={(checked) => onToggleActive(record, checked)}
        />
      ),
      responsive: ["xs", "sm", "md", "lg"],
    },
    {
      title: t("actions"),
      key: "actions",
      render: (_: any, record: DiscountCouponModel) => (
        <Space>
          <Button type="link" onClick={() => onEdit(record)}>
            {t("edit")}
          </Button>
          <Popconfirm
            title={t("are-you-sure-to-delete-this-coupon")}
            onConfirm={() => handleDelete(record.discountCouponId!)}
            okText={t("yes")}
            cancelText={t("no")}
          >
            <Button type="link" danger>
              {t("delete")}
            </Button>
          </Popconfirm>
        </Space>
      ),
      responsive: ["xs", "sm", "md", "lg"],
    },
  ];

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <Typography.Title level={4}>{t("discount-coupons")}</Typography.Title>
        <Button
          type="primary"
          onClick={onCreateClick}
        >
          {t("create-coupon")}
        </Button>
      </div>
      <Table
        rowKey={(record) => record.discountCouponId!.toString()}
        columns={columns}
        dataSource={coupons}
        loading={loading}
        pagination={{ pageSize: 10 }}
        scroll={{ x: 600 }}
      />
      {isModalOpen && (
        <DiscountCouponModal
          open={isModalOpen}
          onSave={onModalSave}
          onCancel={() => setModalOpen(false)}
          defaultValues={editData}
        />
      )}
    </>
  );
}
